import { useEffect, useState, ChangeEvent } from 'react';
import { Box, Typography, Button } from '@mui/joy';
import { Alert } from '../../features/components/Alert';
import Layout from '../../features/components/Layout';
import { CreateNewAssetModal } from '../../components/Modals/CreateNewAssetModal';

import { useGetAssetsQuery } from '../../app/services/admin';
import { useChangeAssetMutation } from '../../app/services/admin';
import AssetsList from '../../features/components/ui/AssetsList/AssetsList';

import { Filters } from './ui/components/Filters';
import { ASSETS_TYPES } from '../../config/consts';
import { TAsset, TAssetType } from '../../config/requestTypes';

export default function Home() {
  const [assetFilter, setAssetFilter] = useState<TAssetType>(
    ASSETS_TYPES[0].value,
  );
  const {
    data: assetsListResponse,
    refetch: assetsListRefetch,
    isLoading: isAssetsLoading,
    isSuccess: isAssetsSuccess,
    error: assetsError,
  } = useGetAssetsQuery({ type: assetFilter });
  const [changeAsset] = useChangeAssetMutation();

  const [assetNameFilter, setAssetNameFilter] = useState('');
  const [assetsList, setAssetsList] = useState<TAsset[] | null>(null);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [isOpenCreateAssetModal, setIsOpenCreateAssetModal] = useState(false);
  const [
    [alertSuccessText, setAlertSuccessText],
    [alertErrorText, setAlertErrorText],
  ] = [useState(''), useState('')];

  const assetsListFiltered = assetsList?.filter((asset: TAsset) =>
    asset.title.toLowerCase().includes(assetNameFilter.toLowerCase()),
  );

  useEffect(() => {
    assetsListRefetch();
  }, [assetFilter]);

  useEffect(() => {
    if (assetsListResponse?.length) {
      const sortedAssetsList = [...assetsListResponse].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      setAssetsList(sortedAssetsList);
    }
  }, [assetsListResponse]);

  const uploadAssetIconHandler = async (id: string, file: File) => {
    const changeAssetResp = await changeAsset({
      id,
      data: { image: file },
    }).unwrap();

    if (changeAssetResp) {
      setAssetsList((prev) => {
        if (!prev) return null;
        return prev.map((asset) =>
          asset.id === id
            ? { ...asset, imageUrl: changeAssetResp.imageUrl }
            : asset,
        );
      });
      setAlertSuccessText('Иконка сохранена!');
      setOpenSuccessAlert(true);
    } else {
      setAlertErrorText('Что-то пошло не так. Изменения не сохранены.');
      setOpenErrorAlert(true);
    }
  };

  const changeStringHandler = async (
    id: string,
    value: string | boolean,
    type: string,
  ) => {
    const changeAssetResp = await changeAsset({
      id,
      data: { [type]: value },
    }).unwrap();

    if (changeAssetResp) {
      setAssetsList((prev) => {
        if (!prev) return null;
        return prev.map((asset) =>
          asset.id === id ? { ...asset, [type]: changeAssetResp[type] } : asset,
        );
      });
      setAlertSuccessText('Изменение успешно сохранено!');
      setOpenSuccessAlert(true);
    } else {
      setAlertErrorText('Что-то пошло не так. Изменения не сохранены.');
      setOpenErrorAlert(true);
    }
  };

  const turnOnOffHandler = (id: string, e: ChangeEvent<HTMLInputElement>) =>
    changeStringHandler(id, !e.target.checked, 'isHidden');

  return (
    <Layout>
      <>
        <Filters
          assetFilter={assetFilter}
          setAssetFilter={setAssetFilter}
          assetNameFilter={assetNameFilter}
          setAssetNameFilter={setAssetNameFilter}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            onClick={() => setIsOpenCreateAssetModal((prev) => !prev)}
            variant="outlined"
          >
            Создать новый актив
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isAssetsLoading ? (
            <Typography>Loading...</Typography>
          ) : isAssetsSuccess && assetsListFiltered ? (
            <AssetsList
              assetsList={assetsListFiltered}
              onUploadIcon={uploadAssetIconHandler}
              onChangeString={changeStringHandler}
              turnOnOff={turnOnOffHandler}
            />
          ) : assetsError &&
            'originalStatus' in assetsError &&
            assetsError.originalStatus !== 404 ? (
            <Typography color="danger">Error loading data</Typography>
          ) : (
            <Typography color="warning">
              At the moment, the wallet list is empty
            </Typography>
          )}
          {!assetsList?.length && (
            <Typography color="warning">
              At the moment, the wallet list is empty
            </Typography>
          )}
        </Box>
      </>

      {/* Modals */}
      <CreateNewAssetModal
        openModal={isOpenCreateAssetModal}
        setOpenModal={setIsOpenCreateAssetModal}
      />

      {/* Alerts */}
      {alertErrorText && (
        <Alert
          open={openErrorAlert}
          setOpen={setOpenErrorAlert}
          color="danger"
          duration={3000}
          text={alertErrorText}
        />
      )}
      {alertSuccessText && (
        <Alert
          open={openSuccessAlert}
          setOpen={setOpenSuccessAlert}
          color="success"
          duration={3000}
          text={alertSuccessText}
        />
      )}
    </Layout>
  );
}
